import { useState, useRef, SetStateAction, Dispatch, useEffect } from 'react'
import cx from 'classnames'
import { useHistory, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import { Formik, FormikProps, Form, Field } from 'formik'
import { capitalize } from 'lodash'
import { UserClient } from 'api/clients/UserClient'
import { setUrlParams, getUrlParams } from 'utils/workspaceUtils'
import { getSearchString } from 'utils/urlUtils'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { CustomDomainWarning } from '../CustomDomainWarning/CustomDomainWarning'
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrowRight24.svg'

import Styles from './ContinueWithEmailOrGmail.module.scss'
import Shared from '../../Shared.module.scss'
import { useQueryParams } from 'hooks/useQueryParams'
import { useAuthRedirect } from 'hooks/useAuthRedirect'
import { LOGIN_PATH, SIGNUP_PATH, DEMO_PATH } from 'paths'
import OauthLoginButton from '../OauthLoginButton/OauthLoginButton'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import { RegistrationType, trackContinueWithLogin, trackContinueWithSignUp } from 'screens/LoginOrSignup/utils'
import { Testimonials } from '../Blaze/Testimonials/Testimonials'
import ImageAssembly from '../Blaze/Testimonials/CartoonTestimonials/ImageAssembly'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Text } from 'blaze-ui/Text/Text'
import { loginAnalyticsParams } from 'screens/LoginOrSignup/utils'
import { useAuth } from 'hooks/UseAuth'
import { OnboardingLoader } from '../OnboardingLoader/OnboardingLoader'
import { EyeOpen, EyeClosed, Mail } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import StylesSignup from 'screens/Signup/Signup.module.scss'
import useLoginOrSignUp from 'screens/LoginOrSignup/hooks/useLoginOrSignUp'
import { useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { authFromResponse } from 'utils'
import { Response } from 'request-dot-js'
import { CustomDomainError } from '../CustomDomainError/CustomDomainError'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

type FormValueType = {
  email: string
}

type Props = {
  emailParam?: string
  setEmail: Dispatch<SetStateAction<string>>
  transitionPage: (page: string) => void
  workspaceInviteToken: string
  redirectTo: string
}

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required()
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Invalid email address'),
})

const ContinueWithEmailOrGmail = observer((props: Props) => {
  const auth = useAuth()
  const { featureFlags } = useRootStore()
  const { emailParam, setEmail, transitionPage, workspaceInviteToken, redirectTo } = props
  const [isProcessing, setIsProcessing] = useState(false)
  const [isGoogle, setIsGoogle] = useState(false)
  const urlParams = getUrlParams()
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isBlockedPopupError, setIsBlockedPopupError] = useState(false)
  const [isBlockedCookiesError, setIsBlockedCookiesError] = useState(false)
  const [focused, setFocused] = useState(false)
  const [isSignUp, setIsSignup] = useState(true)
  const [password, setPassword] = useState('')
  const [localEmail, setLocalEmail] = useState('')
  const [loginFailed, setLoginFailed] = useState(false)
  const [customDomainError, setCustomDomainError] = useState<Response | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const queryClient = useQueryClient()
  const formikRef = useRef<FormikProps<FormValueType>>(null)
  const history = useHistory()
  const location = useLocation()
  const { domain: customDomain, promotion_code: promotionCode } = useQueryParams()
  const { isTabletPortrait, isMobile } = useResponsiveDesign()
  const initialValues = { email: emailParam ?? '' }
  const isOnboardingRedesign = !!featureFlags.ready
  const showDfyBanner = useFlipperFlag('dfy_login_cta')

  useEffect(() => {
    if (!location.search) return

    if (!urlParams) setUrlParams(location.search)

    const searchParams = new URLSearchParams(location.search)
    loginAnalyticsParams.forEach((param) => {
      if (searchParams.has(param)) searchParams.delete(param)
    })

    const paramsString = searchParams.toString()

    !!paramsString.length ? history.push(`${location.pathname}?${paramsString}`) : history.push(location.pathname)
  }, [location.search, urlParams, location.pathname, history])

  const verifyUserExists = async (email: string) => {
    if (email.length === 0) return

    if (!isSignUp) {
      trackContinueWithLogin()
      transitionPage('USER_FOUND')
      return
    }

    const res = await UserClient.verifyUserExists(email, redirectTo)
    if (res.data.success) {
      trackContinueWithSignUp()
      transitionPage('USER_NOT_FOUND')
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Do not unfocus if we are clicking submit button w/in the text input
    if (
      e.relatedTarget &&
      Array.from((e.relatedTarget as HTMLElement).classList).some((className) =>
        className.includes('inlineSubmitButton'),
      )
    )
      return
    setFocused(false)
  }

  useEffect(() => {
    setIsSignup(location.pathname === SIGNUP_PATH)
  }, [location.pathname])

  useEffect(() => {
    setShowErrorMessage(isError || isBlockedPopupError || isBlockedCookiesError)
  }, [setShowErrorMessage, isError, isBlockedPopupError, isBlockedCookiesError])

  useAuthRedirect({ redirect: false, customDomain, disabled: isGoogle })

  const { onLoginOrSignUpSuccess: onLoginSuccess } = useLoginOrSignUp()

  const resetPassword = async () => {
    const emailToUse = localEmail || emailParam
    if (!emailToUse) return

    if (isOnboardingRedesign) {
      showToastMessage('We sent you an email to reset your password, please check your inbox.', 'success')
      await UserClient.sendEmail(emailToUse, 'password_reset', redirectTo)

      return
    }

    await UserClient.sendEmail(emailToUse, 'password_reset', redirectTo)
    transitionPage('RESET_PASSWORD')
  }

  const login = async (email: string, password: string) => {
    if (!email) return

    setIsProcessing(true)

    const res = await client.login(email, password, customDomain)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      const auth = authFromResponse(res.data.data, res.headers)

      const nextPage = await onLoginSuccess(
        auth,
        RegistrationType.EmailAndPassword,
        redirectTo,
        workspaceInviteToken,
        promotionCode,
      )

      queryClient.invalidateQueries('featureFlags')

      setIsProcessing(false)

      if (nextPage) transitionPage(nextPage)
    } else {
      if (customDomain) {
        setCustomDomainError(res)
      } else {
        setLoginFailed(true)
      }
      setIsProcessing(false)
      return
    }
  }

  if (auth) {
    return <OnboardingLoader fadeIn={false} fadeOut={false} />
  }

  let header = 'Sign up for a free 7-day trial'
  if (!isSignUp) header = 'Log in'

  const dfyBannerContent = (
    <>
      <div className={Styles.dfyBannerText}>
        <span className={Styles.dfyHeading}>
          <Heading level={5} color="var(--light-100)">
            Tired of logging in to do it yourself? We'll do it for you.
          </Heading>
        </span>
        <Text variant="secondary" color="var(--light-100)">
          Get a US-based expert to run your marketing so you grow faster, save 80% vs. an agency, and stay focused on
          your business.
        </Text>
      </div>
      <div className={Styles.dfyBannerActions}>
        <Text variant="secondary" color="var(--light-60)">
          Starting at $899/mo.
        </Text>
        <a
          href={`${DEMO_PATH}?${getSearchString({ utm_source: 'product', utm_medium: 'login' })}`}
          className={Styles.dfyButton}
          target="_blank"
          rel="noreferrer"
        >
          <Text variant="secondary" color="var(--dark-90)">
            Talk to a Blaze Done for You Expert
          </Text>
        </a>
      </div>
    </>
  )

  if (isOnboardingRedesign) {
    return (
      <div className={Styles.loginWrapper}>
        <div className={Styles.loginContent}>
          <div className={Styles.loginLeftContent}>
            <div className={StylesSignup.welcomeToBlaze}>
              <Heading level={1}>Sign in to Blaze</Heading>
              <Text color="var(--dark-40)">
                Don't have an account?{' '}
                <button
                  className={StylesSignup.loginLink}
                  data-test-id="switch-login"
                  onClick={() => history.replace({ pathname: SIGNUP_PATH, search: location.search })}
                >
                  Sign up
                </button>
              </Text>
            </div>
            <CustomDomainWarning />
            <div className={cx(Shared.ctaWrapper, Styles.buttonsContainer)}>
              <div className={Styles.googleLoginButton}>
                <OauthLoginButton provider={RegistrationType.Google} workspaceInviteToken={workspaceInviteToken} />
              </div>
              <div className={Styles.facebookLoginButton}>
                <OauthLoginButton provider={RegistrationType.Facebook} workspaceInviteToken={workspaceInviteToken} />
              </div>
              <div className={Styles.appleLoginButton}>
                <OauthLoginButton provider={RegistrationType.Apple} workspaceInviteToken={workspaceInviteToken} />
              </div>

              <div className={StylesSignup.orSeparator}>
                <div className={StylesSignup.orSeparatorLine} />
                <Text variant="label" color="var(--dark-40)">
                  Or sign in with email
                </Text>
                <div className={StylesSignup.orSeparatorLine} />
              </div>

              {showErrorMessage && (
                <div className={Shared.errorContainer}>
                  <p className={Shared.errorMessage}>
                    {isError && 'There was a problem with your request. '}
                    {isBlockedPopupError && 'Your browser blocked a popup. Change this setting. '}
                    {isBlockedCookiesError && 'Enable third party cookies in your browser to use Google sign in. '}
                    <button
                      type="button"
                      onClick={() => {
                        if (isError) setIsError(false)
                        if (isBlockedPopupError) setIsBlockedPopupError(false)
                        if (isBlockedCookiesError) setIsBlockedCookiesError(false)
                        formikRef.current?.resetForm()
                      }}
                    >
                      Try again.
                    </button>
                  </p>
                </div>
              )}
              <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={(data) => {
                  setIsGoogle(false)
                  setLocalEmail(data.email)
                  login(data.email, password)
                }}
                validationSchema={validationSchema}
                validateOnChange
                validateOnMount
              >
                {({ errors, touched }) => {
                  return (
                    <Form noValidate className={cx(Shared.form, Styles.form)}>
                      <div className={cx(Shared.newFieldWrapper, Styles.emailField)}>
                        <div className={Styles.formGap}>
                          <Text variant="label" color="var(--dark-60)">
                            Email
                          </Text>
                          <Field
                            id="email"
                            name="email"
                            size={isMobile ? 'lg' : 'xl'}
                            as={Input}
                            type="email"
                            isValid={!(touched.email && errors.email)}
                            focused={focused}
                            data-heap-id="enter-email"
                            autoFocus
                            onFocus={() => setFocused(true)}
                            onClick={() => setFocused(true)}
                            placeholder="Email"
                            onBlur={handleBlur}
                          />
                        </div>
                      </div>
                      <div className={Shared.newFieldWrapper}>
                        <div className={Styles.formGap}>
                          <Text variant="label" color="var(--dark-60)">
                            Password
                          </Text>
                          <Input
                            name="password"
                            id="password"
                            autoComplete="current-password"
                            size={isMobile ? 'lg' : 'xl'}
                            isValid={!loginFailed}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            placeholder="Password"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              if (loginFailed) {
                                setLoginFailed(false)
                              }
                              setPassword(event.target.value)
                            }}
                          />
                        </div>
                        {password && (
                          <div className={cx(Shared.showPasswordButton, Styles.showPasswordButton)}>
                            {isOnboardingRedesign ? (
                              <IconButton
                                onPress={() => setShowPassword(!showPassword)}
                                variant="tertiary"
                                icon={showPassword ? EyeClosed : EyeOpen}
                                color="var(--dark-40)"
                              />
                            ) : (
                              <Button variant="tertiary" fullWidth onPress={() => setShowPassword(!showPassword)}>
                                <Text variant="label" color="var(--dark-40)">
                                  {showPassword ? 'Hide' : 'Show'}
                                </Text>
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      {customDomainError && <CustomDomainError error={customDomainError} customDomain={customDomain} />}
                      <Button
                        type="submit"
                        isDisabled={isProcessing || !password.length || (touched.email && !!errors.email)}
                        size={isMobile ? 'lg' : 'xl'}
                      >
                        Continue
                      </Button>
                      {touched.email && errors.email && <FormErrorMessage>{capitalize(errors.email)}</FormErrorMessage>}
                      {loginFailed && (
                        <FormErrorMessage className={Styles.resetPasswordError}>
                          <div className={Shared.unauthorizedUser}>
                            Sorry, your password doesn't match. Try again, or{' '}
                          </div>
                          <button type="button" className={Styles.reset} onClick={resetPassword}>
                            Reset password
                          </button>
                        </FormErrorMessage>
                      )}
                    </Form>
                  )
                }}
              </Formik>
            </div>
          </div>
          <div className={Styles.footer}>
            <div className={Styles.termsOfServiceAndPrivacyPolicy}>
              <span>
                <Text variant="label" color="var(--dark-40)">
                  <span className={Styles.firstLine}>By signing in, you agree to the </span>
                  <a
                    tabIndex={0}
                    href="https://www.blaze.ai/terms"
                    target="_blank"
                    rel="noreferrer"
                    className={cx(Shared.smallLink, Styles.smallLink)}
                  >
                    Terms of Service.
                  </a>
                  , and{' '}
                  <a
                    href="https://www.blaze.ai/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className={cx(Shared.smallLink, Styles.smallLink)}
                  >
                    Privacy Policy.
                  </a>
                </Text>
              </span>
            </div>
          </div>
          {showDfyBanner && <div className={Styles.dfyBannerMobile}>{dfyBannerContent}</div>}
        </div>
        <div className={cx(StylesSignup.blazeIllustration, Styles.illustrationContainer)}>
          <img
            className={Styles.illustrationImage}
            alt="blaze illustration"
            src="https://res.cloudinary.com/almanac/image/upload/v1765477725/blaze_assets/virtual_marketer/onboarding/Frame_1171276883_x1tth2.png"
          />
          {showDfyBanner && <div className={Styles.dfyBanner}>{dfyBannerContent}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className={cx(Shared.content, Styles.content)}>
      <div className={cx(Shared.leftPanel, Styles.leftPanel)}>
        <div className={cx(Shared.leftContent, Styles.leftContent)}>
          <div
            id="signup-header"
            className={cx(Styles.title, {
              [Shared.withoutSubTitleOrErrors]: !(isSignUp || showErrorMessage || customDomain),
            })}
          >
            {header}
          </div>
          <CustomDomainWarning />
          <div className={cx(Shared.linkWrapperText, Styles.subheaderContainer)}>
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            <button
              className={Shared.link}
              data-test-id="switch-login"
              onClick={() =>
                history.replace({ pathname: isSignUp ? LOGIN_PATH : SIGNUP_PATH, search: location.search })
              }
            >
              {isSignUp ? 'Login' : 'Join Free'}
            </button>
          </div>
          <div className={Shared.ctaWrapper}>
            <div className={Styles.googleLoginButton}>
              <OauthLoginButton provider={RegistrationType.Google} workspaceInviteToken={workspaceInviteToken} />
            </div>
            <div className={Styles.facebookLoginButton}>
              <OauthLoginButton provider={RegistrationType.Facebook} workspaceInviteToken={workspaceInviteToken} />
            </div>
            <div className={Styles.appleLoginButton}>
              <OauthLoginButton provider={RegistrationType.Apple} workspaceInviteToken={workspaceInviteToken} />
            </div>
            {showErrorMessage && (
              <div className={Shared.errorContainer}>
                <p className={Shared.errorMessage}>
                  {isError && 'There was a problem with your request. '}
                  {isBlockedPopupError && 'Your browser blocked a popup. Change this setting. '}
                  {isBlockedCookiesError && 'Enable third party cookies in your browser to use Google sign in. '}
                  <button
                    type="button"
                    onClick={() => {
                      if (isError) setIsError(false)
                      if (isBlockedPopupError) setIsBlockedPopupError(false)
                      if (isBlockedCookiesError) setIsBlockedCookiesError(false)
                      formikRef.current?.resetForm()
                    }}
                  >
                    Try again.
                  </button>
                </p>
              </div>
            )}
            {showEmailInput ? (
              <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                onSubmit={(data) => {
                  setIsGoogle(false)
                  setIsProcessing(true)
                  transitionPage('USING_EMAIL_AND_PASSWORD')
                  setEmail(data.email)
                  verifyUserExists(data.email)
                }}
                validationSchema={validationSchema}
                validateOnChange
                validateOnMount
              >
                {({ values, errors, touched }) => {
                  return (
                    <Form noValidate className={Shared.form}>
                      <div className={cx(Shared.newFieldWrapper, Styles.emailField)}>
                        <Field
                          id="email"
                          name="email"
                          size={isMobile ? 'lg' : 'xl'}
                          as={Input}
                          type="email"
                          isValid={!(touched.email && errors.email)}
                          focused={focused}
                          data-heap-id="enter-email"
                          autoFocus
                          onFocus={() => setFocused(true)}
                          onClick={() => setFocused(true)}
                          placeholder={isSignUp ? 'Continue with Email' : 'Email'}
                          onBlur={handleBlur}
                        />
                        {!!values.email.length && (
                          <button
                            type="submit"
                            data-test-id={`${isSignUp ? 'signup' : 'login'}-continue-with-email`}
                            data-heap-id={`${isSignUp ? 'signup' : 'login'}-continue-with-email`}
                            className={Styles.inlineSubmitButton}
                            aria-label="Submit"
                            disabled={isProcessing || (touched.email && !!errors.email)}
                          >
                            <div className={Styles.inlineSubmitButtonIcon}>
                              <RightArrowIcon />
                            </div>
                          </button>
                        )}
                      </div>
                      {touched.email && errors.email && <FormErrorMessage>{capitalize(errors.email)}</FormErrorMessage>}
                    </Form>
                  )
                }}
              </Formik>
            ) : (
              <Button size="lg" fullWidth useChildClass variant="secondary" onPress={() => setShowEmailInput(true)}>
                <div className={Styles.mailIcon}>
                  <Mail size={20} />
                </div>
                Continue with Email
              </Button>
            )}
            <div className={Styles.testimonialContainer}>
              {isMobile && <Testimonials currentPage="continueWithEmailOrGmail" newFormat />}
            </div>
          </div>
        </div>
        <div className={Styles.footer}>
          <div className={Styles.termsOfServiceAndPrivacyPolicy}>
            <span>
              <Text variant="label">
                By continuing, you agree to Blaze's{' '}
                <a
                  tabIndex={0}
                  href="https://www.blaze.ai/terms"
                  target="_blank"
                  rel="noreferrer"
                  className={Shared.smallLink}
                >
                  Terms of Service.
                </a>
              </Text>
              <Text variant="label">
                Read our{' '}
                <a
                  href="https://www.blaze.ai/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className={Shared.smallLink}
                >
                  Privacy Policy.
                </a>
              </Text>
            </span>
          </div>
        </div>
      </div>
      {!isTabletPortrait && !isMobile && (
        <ImageAssembly offsetTop={200} page={'continueWithEmailOrGmail'} useSmallerBubble />
      )}
    </div>
  )
})

export default ContinueWithEmailOrGmail
